import React from 'react';
import './search-result-card.less';

import CheckIcon from '@atlaskit/icon/glyph/check';
import CommentIcon from '@atlaskit/icon/glyph/comment';
import LikeIcon from '@atlaskit/icon/glyph/like';
import { AtlasLink } from '../atlas-link';
import { Breadcrumb, TitleSection, TicketType } from '.';
import { highlightText } from '../../utils/highlight-search-result-keyword';
import analytics from '../../utils/analytics';
import { MouseEventButton } from '../../utils/constants';
import { translatableText } from '../../utils/translatable-text';
import { HeaderProps } from '../header';
import { Deployment } from '../../pages/search-results/types';
import { getContentAri } from '../../utils/get-content-ari';

const MOUSE_EVENT_BUTTONS_TO_TRACK = new Set([
    MouseEventButton.LEFT,
    MouseEventButton.MIDDLE
]);

export interface SearchResultCardProps {
    id: string;
    ticketType?: TicketType;
    breadcrumbs?: string[];
    title: string;
    description: string;
    dateModified?: string;
    answerAccepted?: boolean;
    views?: number;
    comments?: number;
    markedUseful?: number;
    href: string;
    searchTerm: string;
    position: number;
    queryId: string;
    versions?: string[];
    products?: any[];
    productIcons?: HeaderProps['productIcons'];
}

export const SearchResultCard: React.FC<SearchResultCardProps> = ({
    id,
    breadcrumbs,
    title,
    description,
    dateModified,
    answerAccepted,
    views,
    comments,
    markedUseful,
    ticketType,
    href,
    searchTerm,
    position,
    queryId,
    products,
    productIcons
}: SearchResultCardProps) => {
    const isFooterVisible = !!(
        answerAccepted ||
        views ||
        comments ||
        markedUseful
    );
    const timeRendered = Date.now();
    const contentAri = getContentAri(id);

    let lozengeText;
    let productIconUrl;

    const getLozengeText = (versions: string[], productName: string) => {
        if (versions?.length === 1) {
            lozengeText = `${productName} ${versions[0]}`;
        } else if (versions?.length > 1) {
            lozengeText = `${productName} ${
                versions[versions.length - 1]
            } & later`;
        } else {
            lozengeText = productName;
        }
    };

    if (products?.length === 1) {
        const { versions, name, key, platform } = products[0];
        getLozengeText(versions, name);
        productIconUrl = productIcons?.[platform as Deployment]?.[key];
    } else if (products?.length && products.length > 1) {
        lozengeText = translatableText.multipleVersionsProducts.text;
    }

    const formattedDate = dateModified
        ? new Date(dateModified).toLocaleDateString(navigator.language)
        : undefined;

    const renderAnswerAccepted = () => {
        if (answerAccepted) {
            return (
                <span
                    className="answer-accepted background-success"
                    data-testid="answer-accepted"
                >
                    <span className="check-icon">
                        <CheckIcon size="small" label="Answer accepted" />
                    </span>
                    <span>Answer Accepted</span>
                </span>
            );
        }
        return null;
    };

    const renderViews = () => {
        if (views) {
            return (
                <span className="views-text" data-testid="views">
                    {views} views
                </span>
            );
        }
        return null;
    };

    const renderComments = () => {
        if (comments) {
            return (
                <span className="comments" data-testid="comments">
                    <CommentIcon size="small" label="Comments" />
                    <span>{comments}</span>
                </span>
            );
        }
        return null;
    };

    const renderMarkedUseful = () => {
        if (markedUseful) {
            return (
                <span className="marked-useful" data-testid="marked-useful">
                    <LikeIcon size="small" label="marked-useful" />
                    <span>{markedUseful} found this helpful</span>
                </span>
            );
        }
        return null;
    };

    const handleOnClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        // Only track analytics for trackable mouse buttons (LEFT and MIDDLE)
        // For other buttons (RIGHT, BACK, FORWARD), prevent analytics tracking as by design these events
        // cannot trigger a new page load.
        if (!MOUSE_EVENT_BUTTONS_TO_TRACK.has(e.button)) {
            // Call preventDefault to signal to AtlasLink's chained handler that tracking should be skipped
            e.preventDefault();
            return;
        }

        const linkContentAri = getContentAri(id);
        analytics.trackEvent('search result', {
            event: 'clicked',
            eventComponent: 'search-result-card',
            actionSubjectId: 'link',
            category: 'search',
            action: 'clicked',
            label: 'search result card',
            position,
            searchTerm,
            linkUrl: href,
            contentId: id,
            timeToClick: (Date.now() - timeRendered) / 1000,
            clickButton: MouseEventButton[e.button],
            queryId,
            linkContentAri,
            link_type: 'card',
            link_content_ari: linkContentAri,
            link_content_entry_id: id,
            link_content_resolved_url: href
        });
    };
    const handleContextMenu = (e: React.MouseEvent<HTMLAnchorElement>) => {
        /*
         * We are consciously choosing to disable the right click context menu so that we can reduce the possibility of
         * loosing analytics events when a user:
         * - Right clicks a search result.
         * - Selects "Open Link in New Tab" or similar.
         *
         * See: https://hello.jira.atlassian.cloud/browse/CSTE-2430?focusedCommentId=13967701
         */
        e.preventDefault();
    };

    return (
        <AtlasLink
            className="search-result-card search-result-card__card-button-wrapper"
            data-testid="search-result-card"
            href={href}
            target="_blank"
            text={title}
            contentLinkAnalytics={{
                payload: {
                    linkType: 'card',
                    context: 'search-results',
                    position,
                    linkContentAri: contentAri,
                    linkContentResolvedUrl: href,
                    parentPageType: 'search-results'
                },
                shouldSkip: false
            }}
            onClick={handleOnClick}
            onAuxClick={handleOnClick}
            onContextMenu={handleContextMenu}
        >
            <div className="search-result-card__card-container">
                <div className="left-section">
                    <Breadcrumb breadcrumbs={breadcrumbs} />
                    <div className="card-content text-gray">
                        <TitleSection
                            ticketType={ticketType}
                            title={title}
                            iconUrl={productIconUrl}
                        />
                        <p
                            className="card-description"
                            data-testid="search-result-card__description"
                        >
                            {highlightText(description)}
                        </p>
                        {formattedDate && (
                            <p
                                className="last-modified-date"
                                data-testid="datemodified"
                            >
                                Last modified on {formattedDate}
                            </p>
                        )}
                        {isFooterVisible && (
                            <div
                                className="card-footer"
                                data-testid="card-footer"
                            >
                                {renderAnswerAccepted()}
                                {renderViews()}
                                {renderComments()}
                                {renderMarkedUseful()}
                            </div>
                        )}
                        <small className="card-url" data-testid="card-url">
                            {href}
                        </small>
                    </div>
                </div>
                <div className="right-section text-gray">
                    <div className="search-result-card__lozenge-container">
                        <span
                            className="search-result-card__lozenge"
                            data-testid="search-result-card__lozenge"
                        >
                            {lozengeText}
                        </span>
                    </div>
                </div>
            </div>
        </AtlasLink>
    );
};
